<template>
  <v-text-field
    v-model="searchField"
    autocomplete="off"
    autofocus
    clearable
    density="compact"
    hide-details
    name="field-search"
    :placeholder="t('placeholders.search_in_box', { box: searchPlaceholder })"
    width="100%"
  />
</template>

<script lang="ts" setup>
import { SECTIONS } from '@/types/sections'
import debounce from 'lodash/debounce'
import last from 'lodash/last'
import { useDeepBoxCoreStore } from '@/stores/deepbox/core.ts'
import { useDeepBoxDeepBoxesBoxesNodesStore } from '@/stores/deepbox/deepboxes/boxes/nodes.ts'
import { useDeepBoxSearchStore } from '@/stores/deepbox/search.ts'
import { useGlobalStore } from '@/stores/global/global.ts'
import { DeviceKey } from '@/plugins/device-detector-js.ts'
import { useBoxBreadcrumbs } from '@/composables/box/box-breadcrumbs'
import { useNodeSection } from '@/composables/node/node-section'

const deepBoxSearchStore = useDeepBoxSearchStore()
const deepBoxCoreStore = useDeepBoxCoreStore()
const deepBoxDeepBoxesBoxesNodesStore = useDeepBoxDeepBoxesBoxesNodesStore()
const globalStore = useGlobalStore()

const device = inject(DeviceKey)
const { t } = useI18n()

const searchField = ref('')

const { boxBreadcrumbsFiltered } = useBoxBreadcrumbs()

const { getNodeSectionTranslation } = useNodeSection()
const searchPlaceholder = computed(() => {
  const breadcrumb = last(boxBreadcrumbsFiltered.value)
  if (breadcrumb && breadcrumb.item) {
    return getNodeSectionTranslation(breadcrumb.item.displayName)
  }
  return ''
})

const searchNodeId = computed(() => {
  const searchNode = last(boxBreadcrumbsFiltered.value)
  if (!searchNode || !searchNode.item) return ''
  if (searchNode.item.nodeId === SECTIONS.INBOX) {
    return deepBoxCoreStore.selectedBox?.roots?.queue
  }
  if (searchNode.item.nodeId === SECTIONS.FILES) {
    return deepBoxCoreStore.selectedBox?.roots?.files
  }
  if (searchNode.item.nodeId === SECTIONS.TRASH) {
    return deepBoxDeepBoxesBoxesNodesStore.getCurrentRootNodeId
  }
  return searchNode.item.nodeId
})

const mobileSearchPayload = computed(() => {
  return {
    nodeId: searchNodeId.value ?? '',
    query: searchField.value,
    offset: 0,
    limit: 50,
  }
})

watch(
  () => searchField.value,
  (newValue) => {
    if (globalStore.mobileSearch && device?.isMobile) {
      deepBoxCoreStore.mobileSearchField = newValue
      searchForMobile()
    }
  },
)

watch(
  () => globalStore.mobileSearch,
  (newValue) => {
    if (!newValue) {
      searchField.value = ''
      // reset search store when out of mobile search
      deepBoxSearchStore.abortPendingSearch()
      deepBoxSearchStore.$reset()
    }
  },
)

const searchForMobile = debounce(async function () {
  await deepBoxSearchStore.fetchSearchResults(mobileSearchPayload.value)
}, 500)
</script>
